import styled, { useTheme } from 'styled-components'

import Select from 'src/stories/Select'
import Tooltip from 'src/stories/Tooltip'
import { Button } from 'src/stories/Button'
import { StyledTabsSearchInput } from './styled'
import { StyledHeaderContainer } from '../styled'
import { Col, Row } from 'src/stories/Layout'
import useMhContext from 'src/contexts/MhContext'
import ConfirmationModal from './Modals/ConfirmationModal'
import useModalNotificationsContext from 'src/contexts/ModalNotificationsContext'
import { HandleTabConversationChangeParams } from 'src/containers/MessagingHub/types'

import { SpamIcon, ArchiveIcon, OpenMessageIcon } from 'src/stories/assets'
import Checkbox from 'src/stories/Checkbox'

export const StyledTabsSearchInputContainer = styled.div(({ theme }) => ({
  padding: `0 ${theme.space(4)}`,
}))

const StyledRow = styled(Row)(({ theme }) => ({
  padding: `0 ${theme.space(4)}`,
  alignItems: 'center',
  marginTop: theme.space(3),
  height: theme.space(8),
}))

export const StyledCol = styled(Col)(() => ({
  margin: 0,
}))

const EditActionsContainer = styled.div(({ theme }) => ({
  height: theme.space(11),
  backgroundColor: theme.colors.base_3,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `0 ${theme.space(4)}`,
}))

const ActionsLabel = styled.p(({ theme }) => ({
  margin: 0,
  color: theme.colors.base_100,
  fontWeight: 500,
  fontSize: '1.5rem',
}))

const ActionIconsContainer = styled.div(() => ({ display: 'flex' }))

const ActionIconsDivider = styled.div(({ theme }) => ({
  width: 0,
  marginRight: theme.space(5),
}))

interface ConversationListHeaderProps {
  conversationsTab: string
  unreadConversationsCount: number
  handleTabConversationChange: (
    params: HandleTabConversationChangeParams
  ) => void
  editMode: boolean
  toggleEditMode: () => void
  selectAll: boolean
  toggleSelectAll: (checked: boolean) => void
  selectedCount: number
  onArchiveClicked: () => void
  onMarkAsSpamClicked: () => void
  onMarkReadClicked: () => void
}

const ConversationListHeader: React.FC<ConversationListHeaderProps> = ({
  conversationsTab,
  unreadConversationsCount,
  handleTabConversationChange,
  editMode,
  toggleEditMode,
  selectAll,
  toggleSelectAll,
  selectedCount,
  onArchiveClicked,
  onMarkAsSpamClicked,
  onMarkReadClicked,
}) => {
  const {
    isDesktop,
    conversationListFilter,
    setConversationListFilter,
    isMediumDesktop,
  } = useMhContext()

  const theme = useTheme()
  const { showModal, closeModal } = useModalNotificationsContext()

  return (
    <StyledHeaderContainer
      data-cy="conversations-list-header"
      height={theme.space(isDesktop ? 31 : 30)}
      $border="right"
      $isMediumDesktop={isMediumDesktop}
    >
      <StyledCol>
        {editMode ? (
          <EditActionsContainer>
            <ActionsLabel data-cy="mh-conversations-selected">
              <span data-cy="mh-conversations-selected-count">
                {selectedCount}
              </span>{' '}
              selected
            </ActionsLabel>
            <ActionIconsContainer>
              <Tooltip
                text="Archive"
                data-cy="mh-archive-button"
                onClick={() => {
                  showModal({
                    dataCy: 'mh-archive-conversation-modal',
                    title: 'Archive',
                    height: 'auto',
                    hideActionButtons: false,
                    modalActionsOptions: {
                      callToAction: {
                        label: 'Accept',
                        onClick: () => {
                          onArchiveClicked()
                          closeModal()
                        },
                      },
                    },
                    customBody: (
                      <ConfirmationModal message="Are you sure you want to archive the conversation(s)?" />
                    ),
                  })
                }}
                widthUnits={20}
                position="bottom"
              >
                <ArchiveIcon />
              </Tooltip>

              <ActionIconsDivider />

              <Tooltip
                text="Mark read"
                data-cy="mh-mark-read-button"
                widthUnits={30}
                onClick={onMarkReadClicked}
                position="bottom"
              >
                <OpenMessageIcon />
              </Tooltip>

              <ActionIconsDivider />

              <Tooltip
                text="Mark as spam"
                data-cy="mh-mark-as-spam-button"
                widthUnits={30}
                onClick={() => {
                  showModal({
                    dataCy: 'mh-spam-conversation-modal',
                    hideActionButtons: false,
                    title: 'Mark as spam',
                    height: 'auto',
                    modalActionsOptions: {
                      callToAction: {
                        label: 'Accept',
                        action: 'danger',
                        onClick: () => {
                          onMarkAsSpamClicked()
                          closeModal()
                        },
                      },
                    },
                    headerColor: 'critical',
                    customBody: (
                      <ConfirmationModal message="Are you sure you want to mark the conversation(s) as spam?" />
                    ),
                  })
                }}
                position="bottom"
              >
                <SpamIcon />
              </Tooltip>
            </ActionIconsContainer>
          </EditActionsContainer>
        ) : (
          <StyledTabsSearchInputContainer>
            <StyledTabsSearchInput
              verticallySpaced={false}
              data-cy="conversation-list-search"
              name="conversation-list-search"
              placeholder={`Search name or phone number`}
              value={conversationListFilter}
              onChange={(e) => setConversationListFilter(e.target.value)}
            />
          </StyledTabsSearchInputContainer>
        )}

        <StyledRow data-cy="mh-select-row">
          {editMode ? (
            <>
              <Checkbox
                name="checkbox"
                size="medium"
                style={{
                  marginRight: theme.space(7),
                }}
                data-cy="mh-select-all-checkbox"
                onChange={(e) => {
                  toggleSelectAll(e.target.checked)
                }}
                checked={selectAll}
              />
              <Button
                label={`${selectAll ? 'Unselect' : 'Select'} All`}
                baseDataAttribute="mh-select-all"
                size="large"
                displayAsText
                onClick={() => {
                  toggleSelectAll(!selectAll)
                }}
                style={{ width: '100%' }}
              />
            </>
          ) : (
            <>
              <Select
                dataCy="mh-conversation-list-header-select"
                initialValue={conversationsTab}
                onChange={(value) =>
                  handleTabConversationChange({
                    mode: 'tab',
                    value,
                  })
                }
                options={[
                  {
                    label: 'Active conversations',
                    value: 'active',
                  },
                  {
                    label: `Unread (${unreadConversationsCount})`,
                    value: 'unread',
                  },
                  {
                    label: 'Archived',
                    value: 'archived',
                  },
                ]}
              />
            </>
          )}
          <Button
            label={editMode ? 'Cancel' : 'Edit'}
            size="large"
            onClick={() => {
              toggleEditMode()
            }}
            baseDataAttribute="mh-toggle-edit-mode"
            displayAsText
            style={{ marginLeft: theme.space(4) }}
          />
        </StyledRow>
      </StyledCol>
    </StyledHeaderContainer>
  )
}

export default ConversationListHeader
